/**
 * Motion — the designer's audit 9.1, 3.9 and 7.2 (`docs/adr/9029`).
 *
 * Five utilities and one refusal cue, each a Tailwind animation over the `motion.*` tokens
 * (`tailwind.config.cjs`), named here so a screen imports a word rather than spelling a class:
 *
 *   fadeUp      an entrance — a toast arriving
 *   growX/growY a bar growing from its baseline (reserved: the bar primitives adopt it once the
 *               shared cells settle, see the ADR)
 *   slideInEnd  a drawer entering from the inline end, 450ms (7.2)
 *   rowExit     a decided row leaving its queue, 300ms (3.9)
 *   shake       a required choice missing when the action was pressed (3.4)
 *
 * **Reduced motion is handled once, not per call site.** `src/styles/index.css` finishes every
 * animation in 0.01ms under `prefers-reduced-motion: reduce`, so each utility lands on its last
 * frame: an entrance simply appears, a leaving row simply goes, and a shake leaves only the
 * colour change that accompanies it. Charts cannot read that stylesheet — ECharts animates on a
 * canvas — so `prefersReducedMotion()` is asked directly and a chart draws without animation.
 */

import { themes } from '../tokens/generated/tokens';

export const MOTION = {
  fadeUp: 'animate-fade-up',
  growX: 'animate-grow-x',
  growY: 'animate-grow-y',
  slideInEnd: 'animate-slide-in-end',
  rowExit: 'animate-row-exit',
  shake: 'animate-shake',
} as const;

export type MotionToken = 'motion-fast' | 'motion-default' | 'motion-exit' | 'motion-shake' | 'motion-enter' | 'motion-chart';

/**
 * A motion token in milliseconds, for the consumers that need a number: a timer that must
 * outlast an animation, and the chart library. Motion is not themed, so the light value is it.
 */
export function motionMs(token: MotionToken): number {
  const value = themes.light[token];
  const ms = Number.parseFloat(value);
  if (!Number.isFinite(ms)) throw new Error(`motion token "${token}" is not a duration: ${value}`);
  return value.trim().endsWith('ms') ? ms : ms * 1000;
}

/**
 * Whether the person asked the operating system to reduce motion. No `matchMedia` (a test
 * environment, an old embedded browser) reads as "reduce": no animation is the safe default,
 * because nothing a screen says depends on one.
 */
export function prefersReducedMotion(): boolean {
  if (typeof globalThis.matchMedia !== 'function') return true;
  return globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
